import React, { useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useResponsive } from 'utils';

import Button from '../Button';
import Typography from '../Typography';
import Card from '../Card';
import Icon from '../Icon';
import './Dialog.css';
import { times } from 'assets/icon';
import { check } from 'assets/img';

export const DIALOG_TYPES = {
  SIMPLE: 'simple',
  CONTENT: 'content',
  CONFIRM: 'confirm',
};

const setBodyStyles = (show) => {
  if (show) {
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '10px';
  } else {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }
};

const Dialog = ({
  show,
  type = DIALOG_TYPES.CONTENT,
  onClose,
  title,
  subtitle,
  headerText,
  children,
  showSuccessIcon = false,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryClick,
  onSecondaryClick,
  disabledPrimaryBtn = false,
  ariaLabel,
  invokingElementId,
  id,
  iconName,
  iconSrc,
  iconFontSize = 20,
  iconColor,
  subheading,
  boxDescription,
  footerText,
  dialogError,
  wrapperStyle,
  contentClassName,
  reinstatementModalContent = null,
}) => {
  const dialogRef = useRef(null);
  const { width } = useResponsive();

  const isTopModal = () => {
    return dialogRef.current === document.body.querySelector('.alf-dialog');
  };

  const invokingElement = invokingElementId
    ? document.getElementById(invokingElementId)
    : document.activeElement;

  const handledClose = () => {
    if (invokingElement) {
      invokingElement.focus();
    }
    onClose();
  };

  const handleClickOutside = (e) => {
    if (e.target === e.currentTarget) {
      handledClose();
    }
  };

  const getFocusableElements = () => {
    if (!dialogRef.current) return [];
    return Array.from(
      dialogRef.current.querySelectorAll(
        'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled])',
      ),
    );
  };

  const handleTabKey = (e) => {
    if (dialogRef.current) {
      const focusableModalElements = getFocusableElements();
      if (focusableModalElements.length === 0) return;
      const firstElement = focusableModalElements[0];
      const lastElement =
        focusableModalElements[focusableModalElements.length - 1];

      if (!focusableModalElements.includes(document.activeElement)) {
        firstElement.focus();
        return e.preventDefault();
      }
      if (!e.shiftKey && document.activeElement === lastElement) {
        firstElement.focus();
        return e.preventDefault();
      }

      if (e.shiftKey && document.activeElement === firstElement) {
        lastElement.focus();
        return e.preventDefault();
      }
    }
  };

  const pressEsc = (e) => {
    if (e.keyCode === 27 && isTopModal()) {
      e.stopPropagation();
      handledClose();
    }
  };

  const keyListenersMap = new Map([
    [27, pressEsc],
    [9, handleTabKey],
  ]);

  useEffect(() => {
    const keyListener = (e) => {
      const listener = keyListenersMap.get(e.keyCode);
      return listener && listener(e);
    };

    setBodyStyles(show);
    document.addEventListener('keydown', keyListener);

    return () => document.removeEventListener('keydown', keyListener);
  }, [show, onClose]);

  const handlePrimary = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    } else {
      onClose();
    }
  };

  const handleSecondary = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }
  };

  const isContent = type === DIALOG_TYPES.CONTENT;
  const isSimple = type === DIALOG_TYPES.SIMPLE;
  const isConfirm = type === DIALOG_TYPES.CONFIRM;
  const hasPrimaryButton = Boolean(primaryButtonText);
  const hasSecondaryButton = Boolean(secondaryButtonText);

  const dialog = (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.001 }}
          className={clsx(
            'alf-dialog',
            `alf-dialog--${type}`,
            width < 415 && 'fullscreen',
          )}
          role="dialog"
          ref={dialogRef}
          aria-modal="true"
          aria-label={ariaLabel}
          id={id}
        >
          <div
            className="dialog__backdrop"
            onClick={handleClickOutside}
            role="presentation"
          />
          <div className="dialog__wrapper relative" style={wrapperStyle}>
            <button
              type="button"
              aria-label="Dismiss"
              className="dialog__close-btn absolute top-[8px] right-[8px]"
              onClick={handledClose}
              data-testid="close-dialog-button"
            >
              <img alt="" src={times} />
            </button>
            {dialogError && (
              <Typography
                type="error"
                className="bg-legacy-errorBackground border border-legacy-errorBorder rounded-[8px] my-[16px] p-[13px]"
              >
                {dialogError}
              </Typography>
            )}
            {(title || headerText) && (
              <div className="dialog__header">
                <div className="dialog__header-content">
                  <Typography
                    type="h1"
                    component="h1"
                    className={`w-full ${isContent ? 'text-left' : 'text-center'} `}
                  >
                    {title}
                  </Typography>
                  {isSimple && showSuccessIcon && (
                    <div className="flex justify-center mt-[20px] mb-[10px]">
                      <img src={check} width="50" alt="Success" />
                    </div>
                  )}
                  {subtitle && (
                    <Typography
                      type="caption1"
                      className={isContent ? 'text-left' : 'text-center'}
                    >
                      {subtitle}
                    </Typography>
                  )}
                  {headerText && (
                    <Typography
                      type="h2-create-account"
                      className={isContent ? 'text-left' : 'text-center'}
                    >
                      {headerText}
                    </Typography>
                  )}
                </div>
              </div>
            )}

            {(children || !isSimple || isConfirm) && (
              <div className={clsx('dialog__content', contentClassName)}>
                {isConfirm ? (
                  <div className="flex flex-col w-full mt-[32px]">
                    <Card className="dialog__confirm-info">
                      <div className="flex w-full min-h-[1.75rem] items-center justify-center">
                        {iconSrc ? (
                          <img
                            src={iconSrc}
                            width={iconFontSize}
                            height={iconFontSize}
                            alt=""
                            aria-hidden="true"
                          />
                        ) : (
                          <Icon
                            name={iconName}
                            fontSize={iconFontSize}
                            color={iconColor}
                            ariaHidden
                          />
                        )}
                      </div>
                      <Typography
                        type="interbold"
                        component="p"
                        className="text-center mb-[8px]"
                      >
                        {subheading}
                      </Typography>
                      <Typography
                        type="caption5"
                        component="p"
                        className="text-center"
                      >
                        {boxDescription}
                      </Typography>
                    </Card>
                    <Typography
                      type="caption5"
                      component="p"
                      className="text-center mt-[24px] mb-[8px]"
                    >
                      {footerText}
                    </Typography>
                  </div>
                ) : isSimple ? (
                  <div className="dialog__simple-content">{children}</div>
                ) : (
                  children
                )}
              </div>
            )}

            {reinstatementModalContent}

            {(hasPrimaryButton || hasSecondaryButton) && (
              <div
                className={`flex flex-row flex-nowrap gap-[12px] items-center ${isSimple || isConfirm ? 'w-full justify-center mt-[38px]' : 'mt-[16px]'}`}
              >
                {hasSecondaryButton && (
                  <Button
                    id={`${id}-secondary-btn`}
                    data-testid="button-go-back"
                    type="tertiary"
                    htmlType="button"
                    onClick={handleSecondary}
                    className="dialog__btn dialog__btn--secondary"
                  >
                    {secondaryButtonText}
                  </Button>
                )}
                {hasPrimaryButton && (
                  <Button
                    id={`${id}-primary-btn`}
                    data-testid="button-ok"
                    type="primary"
                    htmlType="button"
                    onClick={handlePrimary}
                    className={clsx(
                      'dialog__btn',
                      !hasSecondaryButton && 'dialog__btn--full',
                    )}
                    disabled={disabledPrimaryBtn}
                  >
                    {primaryButtonText}
                  </Button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(dialog, document.body);
};

Dialog.propTypes = {
  show: PropTypes.bool.isRequired,
  type: PropTypes.oneOf(Object.values(DIALOG_TYPES)),
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  children: PropTypes.node,
  showSuccessIcon: PropTypes.bool,
  primaryButtonText: PropTypes.string,
  secondaryButtonText: PropTypes.string,
  onPrimaryClick: PropTypes.func,
  onSecondaryClick: PropTypes.func,
  disabledPrimaryBtn: PropTypes.bool,
  ariaLabel: PropTypes.string,
  invokingElementId: PropTypes.string,
  id: PropTypes.string,
  iconName: PropTypes.string,
  iconSrc: PropTypes.string,
  iconFontSize: PropTypes.number,
  iconColor: PropTypes.string,
  subheading: PropTypes.node,
  boxDescription: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  footerText: PropTypes.node,
  dialogError: PropTypes.string,
  wrapperStyle: PropTypes.object,
};

export default Dialog;
